import { KnownErrorCodes } from "./KnownErrorCodes";
import type { RequestContext } from "./client";
import { getMethod, requestHeaders } from "./requestHeaders";
import { OctopusError } from "./resources/index";

export interface FetchOptions {
    requestContext: RequestContext;
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE";
    body?: string | FormData;
    accept?: string;
}

// This implementation still has some limitations compared to our XMLHttpRequest implementation, including
// - Does not support our manual session caching layer (see session.cache)
// - Does not get automatically instrumented as OTel events
export async function dispatchFetch(options: FetchOptions, serverVersion: string | undefined, getAntiForgeryToken: () => string | null) {
    const headers = requestHeaders({
        method: options.method,
        correlationContext: options.requestContext.correlationContext,
        getAntiForgeryToken,
        requestBody: options.body,
        serverVersion,
        accept: options.accept,
    });

    try {
        const response = await fetch(options.url, {
            method: getMethod(options.method).method,
            signal: options.requestContext.abortSignal,
            headers,
            body: options.body,
        });
        if (!response.ok) {
            throw await generateOctopusError(response);
        }
        return response;
    } catch (e) {
        if (e instanceof OctopusError) {
            throw e;
        }

        const error = OctopusError.create(KnownErrorCodes.NetworkError, null);
        error.ErrorMessage = "There was a problem with your request.";
        error.Errors = ["Unable to establish a connection to the Octopus Deploy server. " + "The server may be offline. Please check your network connection."];
        throw error;
    }
}

async function generateOctopusError(response: Response) {
    const bodyText = await response.text();
    const error = OctopusError.create(response.status, JSON.parse(bodyText));

    if (!error.ErrorMessage) {
        error.ErrorMessage = "There was a problem with your request.";
        error.Errors = ["The Octopus Deploy server returned a status of: " + response.status];
        error.FullException = bodyText;
    }

    return error;
}
